import { z } from 'zod';

/**
 * Rollen in Klingerator. `pending` = nieuw account, wacht op goedkeuring door admin.
 * `approver` (Goedkeurder) beoordeelt ingediende producten en mag — net als admin — naar Unit4 schrijven.
 */
export const UserRoleSchema = z.enum(['pending', 'user', 'approver', 'admin']);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const UserSchema = z.object({
  id: z.number().int(),
  email: z.string().email(),
  name: z.string().nullable(),
  role: UserRoleSchema,
  is_active: z.boolean(),
  /** azure | google | local | dev — provider van de eerste login */
  provider: z.string().optional(),
  has_password: z.boolean().optional(),
  last_login_at: z.string().nullable().optional(),
  created_at: z.string().optional(),
});
export type User = z.infer<typeof UserSchema>;

/** Beschikbare loginmethoden (`GET /api/auth/methods`). */
export const AuthMethodsSchema = z.object({
  azure: z.boolean(),
  google: z.boolean(),
  local: z.boolean(),
  registration: z.boolean(),
  dev: z.boolean(),
  environment: z.string(),
});
export type AuthMethods = z.infer<typeof AuthMethodsSchema>;

export const PasswordSchema = z
  .string()
  .min(8, 'Wachtwoord moet minimaal 8 tekens zijn')
  .max(200, 'Wachtwoord is te lang');

export const LoginBodySchema = z.object({
  email: z.string().trim().email('Ongeldig e-mailadres'),
  password: z.string().min(1, 'Wachtwoord is verplicht'),
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

export const RegisterBodySchema = z.object({
  email: z.string().trim().email('Ongeldig e-mailadres'),
  password: PasswordSchema,
  name: z.string().trim().min(2, 'Naam is verplicht').max(200),
});
export type RegisterBody = z.infer<typeof RegisterBodySchema>;

export const Unit4EnvSchema = z.enum(['test', 'prod']);
export type Unit4Env = z.infer<typeof Unit4EnvSchema>;

/** Standaard foutformaat van de API. */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export const PagedSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
  });
export type Paged<T> = { items: T[]; total: number; limit: number; offset: number };

/** Numerieke waarden komen uit Postgres NUMERIC als string; accepteer beide. */
export const NumericSchema = z.union([z.number(), z.string()]).transform((v) => Number(v));
